/**
 * /staff/chat-debug — Stream 11 staff-only dogfooding surface for
 * <ConfirmationCard>.
 *
 * Phase 1 ships ONLY the primitive (no real wiring); this page exists
 * so Leo + the design team can poke at the visual contract — diff
 * rendering, source/reverts lines, CTA layout, auto-focus, Esc-to-
 * cancel — before Phase 2 Action A wires the card to
 * `save_compliance_answer`.
 *
 * GUARD: same shape as `/staff/chat`. The
 * route registers unconditionally so the generated TanStack Router
 * tree stays stable across feature-flag matrices; the page itself
 * gates on `useMeQuery().isStaff`. We intentionally do NOT also gate
 * on the chat-beta env flag — this page renders pure mock data and
 * has no backend dependency, so the only meaningful gate is "are you
 * a CarbonClear staff user".
 */
import React, { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { useMeQuery } from "@/queries/queries";
import ConfirmationCard from "@/components/chat/ConfirmationCard";
import type { FieldChange } from "@/components/chat/ConfirmationCardDiff";

// Three mocked confirmations covering the breadth of Phase 2 actions:
//   1. Single boolean field flip — the canonical save-compliance-answer
//      shape (Phase 2 Action A).
//   2. Multi-field change — proves the diff list stacks vertically
//      without the chrome breaking.
//   3. Empty → populated — exercises the "(empty)" placeholder path.
interface MockConfirmation {
  readonly id: string;
  readonly title: string;
  readonly changes: ReadonlyArray<FieldChange>;
  readonly source: string;
  readonly revertsVia: string;
}

const MOCK_CONFIRMATIONS: ReadonlyArray<MockConfirmation> = [
  {
    id: "single-bool",
    title: "I'll save these to your 2026 LL97 worksheet",
    changes: [
      {
        field: "Boiler replaced after 2020",
        oldValue: "no",
        newValue: "yes",
      },
    ],
    source: "Your message above",
    revertsVia: "Filing → 2026 LL97 → Edit",
  },
  {
    id: "multi-field",
    title: "I'll save these to your 2025 LL88 worksheet",
    changes: [
      {
        field: "Lighting upgrade complete",
        oldValue: "no",
        newValue: "yes",
      },
      {
        field: "Submeters installed",
        oldValue: "partial",
        newValue: "yes (all floors)",
      },
    ],
    source: "Auto-classified from filename",
    revertsVia: "Filing → 2025 LL88 → Edit",
  },
  {
    id: "empty-to-populated",
    title: "I'll file this as 2025 energy bill for BBL 1-00123-0001",
    changes: [
      {
        field: "Annual electricity (kWh)",
        oldValue: null,
        newValue: "1,284,500",
      },
    ],
    source: "Parsed from utility bill PDF",
    revertsVia: "Documents → Trash",
  },
];

const ChatDebugPage: React.FC = () => {
  // `lastAction` records the most-recent CTA invocation per card so
  // the staff dogfooder can verify the handler wiring without
  // popping a console open. Plain object — no need for a state
  // machine, this is a debug surface.
  const [lastAction, setLastAction] = useState<Record<string, string>>({});
  const recordAction = (cardId: string, action: string) => {
    setLastAction((prev) => ({ ...prev, [cardId]: action }));
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold">
          ConfirmationCard — Stream 11 dogfood surface
        </h1>
        <p className="text-sm text-muted-foreground">
          Mock data only. Phase 2 Action A wires this to
          {" "}
          <code className="font-mono">save_compliance_answer</code>. CTAs
          here are no-ops — click them to see the &ldquo;last action&rdquo;
          line update.
        </p>
      </header>
      {MOCK_CONFIRMATIONS.map((mock) => (
        <section
          key={mock.id}
          className="flex flex-col gap-2"
          data-testid={`mock-section-${mock.id}`}
        >
          <ConfirmationCard
            title={mock.title}
            changes={mock.changes}
            source={mock.source}
            revertsVia={mock.revertsVia}
            onConfirm={() => recordAction(mock.id, "Confirm")}
            onEdit={() => recordAction(mock.id, "Edit")}
            onCancel={() => recordAction(mock.id, "Cancel")}
          />
          <p
            className="text-xs text-muted-foreground"
            data-testid={`mock-action-${mock.id}`}
          >
            Last action:{" "}
            <span className="font-mono">
              {lastAction[mock.id] ?? "(none)"}
            </span>
          </p>
        </section>
      ))}
    </div>
  );
};

const NotStaff: React.FC = () => (
  <div className="flex w-full min-h-screen items-center justify-center p-12 text-sm text-muted-foreground">
    <p>This debug surface is staff-only.</p>
  </div>
);

const ChatDebugPageGuarded: React.FC = () => {
  const { data: user, isLoading } = useMeQuery();
  if (isLoading) return null;
  if (!user?.isStaff) return <NotStaff />;
  return <ChatDebugPage />;
};

const ChatDebugRoute = () => <ChatDebugPageGuarded />;

export const Route = createFileRoute("/staff/chat-debug")({
  component: ChatDebugRoute,
});

export default ChatDebugPageGuarded;
export { ChatDebugPage };
